import { openCookiePreferences } from "../lib/consent";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { SITE } from "../data/stories";
import Reveal from "./Reveal";

const FOOTER_CATEGORIES = [
  { id: "all-news", label: "All News", to: "/category/all-news" },
  { id: "gambia-news", label: "Gambia News", to: "/category/gambia-news" },
  { id: "politics", label: "Politics", to: "/category/politics" },
  { id: "sports", label: "Sports", to: "/category/sports" },
  { id: "opinions", label: "Opinions", to: "/category/opinions" },
  { id: "bantaba", label: "Bantaba", to: "/category/bantaba" },
  { id: "sponsored", label: "Sponsored Content", to: "/category/advertisement" },
];

const FOOTER_PAGES = [
  { id: "about", label: "About Us", to: "/about-us" },
  { id: "advertise", label: "Advertise with us", to: "/advertise-with-us" },
  { id: "contact", label: "Contact Us", to: "/contact-us" },
  { id: "privacy", label: "Privacy Policy", to: "/privacy-policy" },
  { id: "choices", label: "Your Privacy Choices", to: "/your-privacy-choices" },
  { id: "corrections", label: "Corrections & Feedback", to: "/corrections-feedback" },
];

const FooterCol = ({ title, testid, children }) => (
  <div data-testid={testid}>
    <h3 className="font-display text-xl font-semibold text-white">{title}</h3>
    {children}
  </div>
);

export const Footer = () => (
  <footer data-testid="footer" className="bg-[#0B0B0B] text-neutral-300">
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-16 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 lg:pr-10" data-testid="footer-about">
          <Link to="/" aria-label="The Standard — home" data-testid="footer-logo-link">
            <span className="font-blackletter text-[26px] leading-none tracking-[1px] text-white">
              The Standard
            </span>
          </Link>
          <p className="mt-5 text-[13px] leading-relaxed text-neutral-300">
            {SITE.about}
          </p>
          <div className="mt-6 flex items-center gap-3">
            {[
              { icon: Facebook, href: SITE.socials.facebook, id: "facebook" },
              { icon: Twitter, href: SITE.socials.x, id: "x" },
              { icon: Youtube, href: SITE.socials.youtube, id: "youtube" },
            ].map(({ icon: Icon, href, id }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={id}
                data-testid={`footer-social-${id}`}
                className="w-9 h-9 rounded-[7px] border border-white/15 flex items-center justify-center text-white/70 hover:bg-[#cf0a0a] hover:border-[#cf0a0a] hover:text-white transition-colors duration-300"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <FooterCol title="Categories" testid="footer-col-categories">
            <ul className="mt-5 space-y-3">
              {FOOTER_CATEGORIES.map((c) => (
                <li key={c.id}>
                  <Link
                    to={c.to}
                    data-testid={`footer-category-${c.id}`}
                    className="text-[13px] text-white/85 hover:text-[#ff4d64] transition-colors duration-300"
                  >
                    {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterCol>
        </div>

        <div className="lg:col-span-2">
          <FooterCol title="Pages" testid="footer-col-pages">
            <ul className="mt-5 space-y-3">
              {FOOTER_PAGES.map((p) => (
                <li key={p.id}>
                  <Link
                    to={p.to}
                    data-testid={`footer-page-${p.id}`}
                    className="text-[13px] text-white/85 hover:text-[#ff4d64] transition-colors duration-300"
                  >
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterCol>
        </div>

        <div className="lg:col-span-3">
          <FooterCol title="Contact" testid="footer-col-contact">
            <ul className="mt-5 space-y-3.5 text-[13px]">
              <li data-testid="footer-contact-phone" className="flex items-start gap-2.5 text-white/85">
                <Phone size={13} className="text-[#cf0a0a] mt-0.5 shrink-0" />
                <span className="flex flex-wrap gap-x-1">
                  {SITE.telephone.split(" / ").map((num, i, arr) => (
                    <span key={num}>
                      <a
                        href={`tel:${num.replace(/\s/g, "")}`}
                        className="hover:text-[#ff4d64] transition-colors duration-300"
                      >
                        {num}
                      </a>
                      {i < arr.length - 1 && <span className="text-white/50"> / </span>}
                    </span>
                  ))}
                </span>
              </li>
              <li>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-contact-email"
                  className="flex items-start gap-2.5 text-white/85 hover:text-[#ff4d64] transition-colors duration-300"
                >
                  <Mail size={13} className="text-[#cf0a0a] mt-0.5 shrink-0" />
                  {SITE.email}
                </a>
              </li>
              <li data-testid="footer-contact-address" className="flex items-start gap-2.5 text-white/70">
                <MapPin size={13} className="text-[#cf0a0a] mt-0.5 shrink-0" />
                Kairaba Avenue, Fajara, The Gambia
              </li>
            </ul>
          </FooterCol>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-white/50">
        <p className="flex flex-wrap items-center justify-center gap-2" data-testid="footer-copyright">
          {SITE.copyright}
          <span className="hidden text-white/20 sm:inline">·</span>
          <span data-testid="footer-credit" className="hidden sm:inline-flex items-center gap-2">
            Site by{" "}
            <a
              href="https://www.digitech.gm/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-credit-link"
              className="text-white/70 hover:text-[#cf0a0a] transition-colors"
            >
              <img src="/digitech-logo.svg" alt="DigiTech Solutions" width="76" height="18" className="block h-auto w-[76px]" />
            </a>
          </span>
        </p>
        <p className="text-[10px] text-white/50" data-testid="weather-credit">Weather: <a href="https://www.met.no/" target="_blank" rel="noreferrer" className="underline hover:text-white">MET Norway</a> · <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer" className="underline hover:text-white">CC BY 4.0</a> · Forecast temperatures rounded for display.</p>
        <nav
          data-testid="footer-legal-links"
          className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
        >
          {[
            { id: "privacy-policy", to: "/privacy-policy", label: "Privacy Policy" },
            { id: "privacy-choices", to: "/your-privacy-choices", label: "Your Privacy Choices" },
            { id: "contact", to: "/contact-us", label: "Contact Us" },
            { id: "corrections", to: "/corrections-feedback", label: "Corrections & Feedback" },
          ].map((l) => (
            <Link
              key={l.id}
              to={l.to}
              onClick={l.id === "privacy-choices" ? openCookiePreferences : undefined}
              data-testid={`footer-legal-${l.id}`}
              className="hover:text-white transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>
          <span data-testid="footer-credit-mobile" className="inline-flex items-center gap-2 sm:hidden">
            Site by{" "}
            <a
              href="https://www.digitech.gm/"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-credit-link-mobile"
              className="text-white/70 hover:text-[#cf0a0a] transition-colors"
            >
              <img src="/digitech-logo.svg" alt="DigiTech Solutions" width="76" height="18" className="block h-auto w-[76px]" />
            </a>
          </span>
      </div>
    </div>
  </footer>
);

export default Footer;
